import React from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import Logo from "../Assets/logo01.png";
import { useAuth } from "./context/AuthProvider";
import toast from "react-hot-toast";

const StudentPackingSlips = ({ order, students, setShowInvoice }) => {
  const { setIsLoading } = useAuth();

  const downloadAllPackingSlips = async () => {
    const pdf = new jsPDF("p", "mm", "a5");
    let isFirstPage = true;

    setIsLoading(true);

    try {
      for (const [index] of students.entries()) {
        const slipElement = document.getElementById(`packing-slip-${index}`);
        if (!slipElement) continue;

        const canvas = await html2canvas(slipElement, {
          scale: 3, // 🔥 Higher scale for clearer PDF text
          useCORS: true,
        });

        const imgData = canvas.toDataURL("image/png");
        const imgWidth = 148;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        if (!isFirstPage) pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);

        isFirstPage = false;
      }

      pdf.save("Student_Packing_Slips.pdf");
      toast.success("Packing slips downloaded");
      setShowInvoice(false);
    } catch (error) {
      console.error(error);
      toast.error("Failed to generate packing slips");
    } finally {
      setIsLoading(false);
    }
  };

  const pay = (payments = [], method) =>
    payments
      .filter((p) => p.method === method)
      .reduce((sum, p) => sum + p.amount, 0);

  return (
    <>
      {/* ACTION BUTTONS */}
      <div className="mt-6 flex justify-end gap-4 px-6">
        <button
          className="bg-gray-200 px-4 py-2 rounded hover:bg-gray-300"
          onClick={() => setShowInvoice(false)}
        >
          Close
        </button>

        <button
          onClick={downloadAllPackingSlips}
          className="bg-blue-600 text-white px-6 py-2 rounded font-semibold hover:bg-blue-500"
        >
          Download PDF
        </button>
      </div>

      {/* PACKING SLIPS */}
      {students.map((student, index) => (
        <div key={index} className="my-10">
          <div
            id={`packing-slip-${index}`}
            className="mx-auto bg-white border p-6"
            style={{
              width: "148mm",
              fontSize: "14px", // 🔥 Bigger base font
              lineHeight: "1.4",
            }}
          >
            {/* HEADER */}
            <div className="flex justify-between items-center border-b pb-3 mb-4">
              <img src={Logo} alt="Logo" className="w-32" />
              <div className="text-right">
                <h1 className="text-2xl font-bold">PACKING SLIP</h1>
                <p className="text-sm">
                  Date: {new Date().toLocaleDateString()}
                </p>
              </div>
            </div>

            {/* STUDENT INFO */}
            <div className="grid grid-cols-2 gap-3 mb-5">
              <div>
                <p><strong>Roll No:</strong> {student.rollNo}</p>
                <p><strong>Name:</strong> {student.name}</p>
                <p><strong>Class:</strong> {student.class}</p>
                <p><strong>House:</strong> {student.house}</p>
              </div>
              <div className="text-right">
                <p><strong>School:</strong> {order?.industry?.name}</p>
                <p><strong>Phone:</strong> {student.phone}</p>
                <p><strong>Slip No:</strong> #{index + 1}</p>
              </div>
            </div>

            {/* PRODUCTS TABLE */}
            <table className="w-full border-collapse border border-black mb-5">
              <thead>
                <tr className="bg-gray-200">
                  <th className="border border-black  text-left">
                    S.No
                  </th>
                  <th className="border border-black  text-left">
                    Product Name
                  </th>
                  <th className="border border-black  text-center">
                    Qty
                  </th>
                </tr>
              </thead>
              <tbody>
                {student.products?.length ? (
                  student.products.map((item, idx) => (
                    <tr key={idx}>
                      <td className="border border-black ">
                        {idx + 1}
                      </td>
                      <td className="border border-black ">
                        {item.product?.name || "Custom Product"}
                      </td>
                      <td className="border border-black  text-center text-lg font-semibold">
                        {item.quantity}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="3"
                      className="border border-black text-center py-4"
                    >
                      No products
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* PAYMENT SUMMARY */}
            <div className="border border-black p-2 mb-5">
              <div className="flex justify-between">
                <span>Cash</span>
                <span>{pay(student?.paymentDetails?.payments, "cash")}</span>
              </div>
              <div className="flex justify-between">
                <span>GPay</span>
                <span>{pay(student?.paymentDetails?.payments, "gpay")}</span>
              </div>
              <div className="flex justify-between">
                <span>Unpaid</span>
                <span>{student?.paymentDetails?.balance}</span>
              </div>
              <div className="flex justify-between font-bold border-t mt-2 pt-2">
                <span>Total</span>
                <span>{student?.paymentDetails?.totalPrice}</span>
              </div>
            </div>

            {/* FOOTER */}
            <div className="grid grid-cols-2 gap-6 mt-6">
              <div>
                <p className="mb-6">Packed By: __________________</p>
                <p>Date: __________________</p>
              </div>
              <div className="text-right">
                <p className="mb-6">Checked By: ________________</p>
                <p>Signature: ________________</p>
              </div>
            </div>

            <p className="text-center text-sm text-gray-500 mt-6">
              *Packing slip*
            </p>
          </div>
        </div>
      ))}
    </>
  );
};

export default StudentPackingSlips;
